/**
 * @fileoverview Searchable, module-aware palette for data-bound form widgets.
 * @module ui-admin/components/form-builder/WidgetPalette
 *
 * Responsibilities:
 * - List registry widgets available for the selected template type and enabled modules.
 * - Add individual or grouped widgets by click, drag, or bulk action.
 * Dependencies: React, @dnd-kit/core, lucide-react, FeatureTierContext, widget registry.
 * Architecture: Builder presentation component; the host supplies fields and add callbacks.
 * Notes: Widgets already represented on the canvas are marked and excluded from bulk additions.
 */
// ============================================================
// Widget Palette — Pre-built Data-Bound Widget Library
// Renders collapsible category groups of pre-built widgets
// in the form builder palette. Supports search, module gating,
// drag-and-drop, click-to-add, and "Add All in Group".
// ============================================================

import React, { useState, useMemo, useCallback } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { ChevronRight, Search, X, Database, Plus, Check, AlertCircle } from 'lucide-react';
import type { DataBoundWidgetDefinition, TemplateType, FormField } from '../../types/templateTypes';
import { useFeatureTier } from '../../contexts/FeatureTierContext';
import {
  DATA_BOUND_WIDGET_REGISTRY,
  getVisibleWidgets,
  WIDGET_CATEGORIES,
} from '../../data/dataBoundWidgetRegistry';

// ---------------------------------------------------------------------------
// Draggable Widget Item
// ---------------------------------------------------------------------------

function DraggableWidgetItem({
  widget,
  isOnCanvas,
  onAdd,
}: {
  widget: DataBoundWidgetDefinition;
  isOnCanvas: boolean;
  onAdd: (widget: DataBoundWidgetDefinition) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `widget-${widget.widgetId}`,
    data: {
      source: 'canvasPaletteWidget',
      type: 'palette',
      fieldType: 'dataBound' as const,
      widgetId: widget.widgetId,
      widget,
    },
  });

  return (
    <button
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => !isOnCanvas && onAdd(widget)}
      title={widget.description || widget.label}
      className={`w-full flex items-center gap-2 px-4 py-1.5 text-sm transition-colors cursor-grab active:cursor-grabbing ${
        isDragging ? 'opacity-40' : ''
      } ${
        isOnCanvas
          ? 'text-gray-400 bg-gray-50/50'
          : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'
      }`}
    >
      <Database size={12} className="shrink-0 text-gray-400" />
      <span className="truncate flex-1 text-left">{widget.label}</span>
      {isOnCanvas && <Check size={12} className="shrink-0 text-green-500" />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Widget Sub-Group
// ---------------------------------------------------------------------------

function WidgetSubGroup({
  categoryLabel,
  widgets,
  canvasWidgetIds,
  isExpanded,
  onToggle,
  onAddWidget,
  onAddAll,
}: {
  categoryLabel: string;
  widgets: DataBoundWidgetDefinition[];
  canvasWidgetIds: Set<string>;
  isExpanded: boolean;
  onToggle: () => void;
  onAddWidget: (widget: DataBoundWidgetDefinition) => void;
  onAddAll: (widgets: DataBoundWidgetDefinition[]) => void;
}) {
  const shortLabel = categoryLabel.includes(' — ')
    ? categoryLabel.split(' — ')[1]
    : categoryLabel;

  const allOnCanvas = widgets.every(w => canvasWidgetIds.has(w.widgetId));
  const addableWidgets = widgets.filter(w => !canvasWidgetIds.has(w.widgetId));

  return (
    <div>
      <div className="flex items-center">
        <button
          onClick={onToggle}
          className="flex-1 flex items-center gap-1 px-3 py-1 text-[11px] font-medium text-gray-500 hover:bg-gray-50"
        >
          <ChevronRight
            size={10}
            className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          />
          <span>{shortLabel}</span>
          <span className="text-[10px] text-gray-400 ml-1">({widgets.length})</span>
        </button>
        {!allOnCanvas && addableWidgets.length > 0 && (
          <button
            onClick={() => onAddAll(addableWidgets)}
            title={`Add all ${addableWidgets.length} widgets in ${shortLabel}`}
            className="px-2 py-1 text-[10px] text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
          >
            <Plus size={10} className="inline -mt-px" /> Add All
          </button>
        )}
      </div>
      {isExpanded && (
        <div className="pb-0.5">
          {widgets.map(w => (
            <DraggableWidgetItem
              key={w.widgetId}
              widget={w}
              isOnCanvas={canvasWidgetIds.has(w.widgetId)}
              onAdd={onAddWidget}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Widget Palette
// ---------------------------------------------------------------------------

interface WidgetPaletteProps {
  templateType: TemplateType;
  fields: FormField[];
  onAddWidget: (widget: DataBoundWidgetDefinition) => void;
  onAddWidgets: (widgets: DataBoundWidgetDefinition[]) => void;
}

/**
 * @component WidgetPalette
 * Purpose: Help template authors find and add data-bound widgets to a form.
 * Props: Template type, current form fields, and individual/bulk add callbacks.
 * Behavior: Filters by enabled modules and search text, supports drag-and-drop and add-all groups.
 * Data Source: local (widget registry, feature-tier context, and supplied form fields).
 * Constraints: The host owns field creation and canvas updates.
 * Performance: Memoizes canvas IDs, visible widgets, and search results.
 */
export function WidgetPalette({
  templateType,
  fields,
  onAddWidget,
  onAddWidgets,
}: WidgetPaletteProps) {
  const { hasModule } = useFeatureTier();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Determine which widget IDs are already on the canvas
  const canvasWidgetIds = useMemo(() => {
    const ids = new Set<string>();
    for (const f of fields) {
      if (f.type === 'dataBound' && f.extras?.widgetId) {
        ids.add(f.extras.widgetId);
      }
    }
    return ids;
  }, [fields]);

  // Get visible widgets based on template type and enabled modules
  const visibleWidgets = useMemo(
    () => getVisibleWidgets(templateType, hasModule),
    [templateType, hasModule],
  );

  // Apply search filter
  const filteredWidgets = useMemo(() => {
    if (!searchTerm.trim()) return visibleWidgets;
    const term = searchTerm.toLowerCase();
    return visibleWidgets.filter(
      w =>
        w.label.toLowerCase().includes(term) ||
        (w.description && w.description.toLowerCase().includes(term)),
    );
  }, [visibleWidgets, searchTerm]);

  // Group widgets by category, preserving order
  const groupedWidgets = useMemo(() => {
    const groups: { category: string; widgets: DataBoundWidgetDefinition[] }[] = [];
    const seen = new Set<string>();
    for (const w of filteredWidgets) {
      if (!seen.has(w.widgetCategory)) {
        seen.add(w.widgetCategory);
        groups.push({ category: w.widgetCategory, widgets: [] });
      }
      groups.find(g => g.category === w.widgetCategory)!.widgets.push(w);
    }
    return groups;
  }, [filteredWidgets]);

  // Separate entity vs event/case groups
  const entityGroups = useMemo(
    () => groupedWidgets.filter(g => g.category.startsWith('Entity')),
    [groupedWidgets],
  );
  const eventGroups = useMemo(
    () => groupedWidgets.filter(g => g.category.startsWith('Event')),
    [groupedWidgets],
  );

  const toggleGroup = useCallback((cat: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }, []);

  // When searching, auto-expand all groups
  const isSearching = searchTerm.trim().length > 0;

  const renderSection = (
    label: string,
    groups: { category: string; widgets: DataBoundWidgetDefinition[] }[],
    sectionExpanded: boolean,
    onToggleSection: () => void,
  ) => {
    if (groups.length === 0) return null;
    const totalWidgets = groups.reduce((sum, g) => sum + g.widgets.length, 0);

    return (
      <div>
        <button
          onClick={onToggleSection}
          className="w-full flex items-center justify-between px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
        >
          <span className="flex items-center gap-1.5">
            <Database size={12} className="text-orange-500" />
            {label}
            <span className="text-[10px] text-gray-400">({totalWidgets})</span>
          </span>
          <ChevronRight
            size={12}
            className={`transition-transform ${sectionExpanded ? 'rotate-90' : ''}`}
          />
        </button>
        {sectionExpanded && (
          <div className="pl-1">
            {groups.map(g => (
              <WidgetSubGroup
                key={g.category}
                categoryLabel={g.category}
                widgets={g.widgets}
                canvasWidgetIds={canvasWidgetIds}
                isExpanded={isSearching || expandedGroups.has(g.category)}
                onToggle={() => toggleGroup(g.category)}
                onAddWidget={onAddWidget}
                onAddAll={onAddWidgets}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const [entitySectionExpanded, setEntitySectionExpanded] = useState(false);
  const [eventSectionExpanded, setEventSectionExpanded] = useState(false);

  if (visibleWidgets.length === 0) {
    return (
      <div className="px-3 py-2 text-[11px] text-gray-400 flex items-center gap-1.5">
        <AlertCircle size={12} />
        No data widgets available for this template type.
      </div>
    );
  }

  return (
    <div>
      {/* Search input */}
      <div className="px-2 py-1.5">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Filter widgets…"
            className="w-full pl-6 pr-6 py-1 text-[11px] border border-gray-200 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary) bg-white"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={12} />
            </button>
          )}
        </div>
        {isSearching && (
          <p className="text-[10px] text-gray-400 mt-0.5 px-0.5">
            {filteredWidgets.length} of {visibleWidgets.length} widgets
          </p>
        )}
      </div>

      {/* Entity Data section */}
      {renderSection(
        'Entity Data',
        entityGroups,
        isSearching || entitySectionExpanded,
        () => setEntitySectionExpanded(prev => !prev),
      )}

      {/* Event Data section */}
      {renderSection(
        'Event Data',
        eventGroups,
        isSearching || eventSectionExpanded,
        () => setEventSectionExpanded(prev => !prev),
      )}
    </div>
  );
}
